'use client';

import { AppsIcon } from '@otter-ai/den-icons';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo } from 'react';
import { DesktopAutoConnectProvider } from '@/desktop/providers/DesktopAutoConnectProvider';
import { ContentContainer } from '@/features/account-settings/shared/components/ContentContainer';
import { SectionalErrorBoundary } from '@/shared/components/SectionalErrorBoundary';
import { useScrollToLocationHashOnLoad } from '@/shared/hooks/useScrollToLocationHashOnLoad';
import { SettingsPageHeader } from '../../shared/components/SettingsPageHeader';
import { useAppsSettings } from '../hooks/useAppsSettings';
import { useAppsSettingsHandleOAuthReturn } from '../hooks/useAppsSettingsHandleOAuthReturn';
import { useSalesforceSettings } from '../hooks/useSalesforceSettings';
import { useWorkatoAppSettingsModal } from '../hooks/useWorkatoAppSettingsModal';
import { useWorkatoApps } from '../hooks/useWorkatoApps';
import type { AppRow } from '../type/AppRow';
import { createAppRowLoggerName } from '../utils/createAppRowLoggerName';

export function ConnectedAppsSettings() {
  useAppsSettingsHandleOAuthReturn();
  const t = useTranslations('settings:integrations');
  const { appRows, isLoading } = useAppsSettings();
  const {
    openSalesforceTeamManageModal,
    openSalesforceMemberManageModal,
    isLoading: isSalesforceSettingsLoading,
  } = useSalesforceSettings();

  const searchParams = useSearchParams();
  const router = useRouter();
  const { workatoApps } = useWorkatoApps();
  const { openWorkatoAppSettingsModal } = useWorkatoAppSettingsModal();

  // Handle open_workato_app_id query param (from desktop app redirect)
  useEffect(() => {
    const workatoAppId = searchParams.get('open_workato_app_id');
    if (workatoAppId && workatoApps) {
      const app = workatoApps.find((a) => String(a.id) === workatoAppId);
      if (app) {
        openWorkatoAppSettingsModal(app);
        // Clean up the query param
        const newParams = new URLSearchParams(searchParams.toString());
        newParams.delete('open_workato_app_id');
        router.replace(`?${newParams.toString()}`);
      }
    }
  }, [searchParams, workatoApps, openWorkatoAppSettingsModal, router]);

  useScrollToLocationHashOnLoad({ isLoading });

  // Handle redirect from Salesforce AI chat intro dialog
  useEffect(() => {
    // Wait until both loading states are complete
    if (isLoading || isSalesforceSettingsLoading) {
      return;
    }

    const redirectModal = localStorage.getItem(
      'salesforce_ai_chat_intro_redirect_modal',
    );

    if (redirectModal) {
      // Clear the redirect URL immediately to prevent multiple opens
      localStorage.removeItem('salesforce_ai_chat_intro_redirect_modal');

      // Determine which modal to open based on the URL
      if (redirectModal === 'salesforce_admin') {
        openSalesforceTeamManageModal().catch((error) => {
          // Log error but don't show to user as this is an automatic action
          console.error('Failed to open Salesforce team manage modal:', error);
        });
      } else if (redirectModal === 'salesforce_member') {
        openSalesforceMemberManageModal().catch((error) => {
          // Log error but don't show to user as this is an automatic action
          console.error(
            'Failed to open Salesforce member manage modal:',
            error,
          );
        });
      }
    }
  }, [
    isLoading,
    isSalesforceSettingsLoading,
    openSalesforceTeamManageModal,
    openSalesforceMemberManageModal,
  ]);

  const sortedAppRows = useSortAppRows(appRows, 'angular');

  return (
    <ContentContainer>
      <div className="w-full">
        <SettingsPageHeader
          title={t('title')}
          description={t('description')}
          Icon={AppsIcon}
        />
        <DesktopAutoConnectProvider>
          {sortedAppRows.map(({ AppComponent, key, name }) => (
            <SectionalErrorBoundary
              key={key}
              loggerName={createAppRowLoggerName(name)}
            >
              <AppComponent />
            </SectionalErrorBoundary>
          ))}
        </DesktopAutoConnectProvider>
      </div>
    </ContentContainer>
  );
}

/**
 * The keys of how the apps appear in the Angular app.
 *
 * In Angular, the Workato apps are sorted by their raw name.
 * At this point, we don't have the raw name
 */
const AngularOrder: ReadonlyMap<string | number, number> = new Map<
  string | number,
  number
>(
  [
    'zapier',
    'workato',
    'slack',
    'salesforce',
    'hubspot',
    'zoom',
    'dropbox',
    'google',
    'google_docs',
    'gmail',
    'microsoft',
    'otter_api',
  ].map((key, index) => [key, index]),
);

/**
 * Sort the apps by name or in the hard-coded Angular order.
 */
function useSortAppRows(
  appRows: AppRow[],
  orderBy: 'name' | 'angular',
): AppRow[] {
  return useMemo(() => {
    switch (orderBy) {
      case 'name':
        return appRows.sort((a, b) => a.name.localeCompare(b.name));
      case 'angular':
        return appRows.sort(compareAngularAppRows);
    }
  }, [appRows, orderBy]);
}

function compareAngularAppRows(a: AppRow, b: AppRow): number {
  const aOrder = getAngularOrder(a.key);
  const bOrder = getAngularOrder(b.key);
  if (
    aOrder === AngularOrder.get('workato') &&
    bOrder === AngularOrder.get('workato')
  ) {
    // Using the sorting method used in Angular
    const orderedKeys = [a.name ?? a.meta?.name, b.name ?? b.meta?.name].sort();
    return (
      orderedKeys.indexOf(a.name ?? a.meta?.name) -
      orderedKeys.indexOf(b.name ?? b.meta?.name)
    );
  }
  return aOrder - bOrder;
}

function getAngularOrder(key: string | number): number {
  if (typeof key === 'string' && key.startsWith('workato')) {
    return AngularOrder.get('workato') ?? Number.POSITIVE_INFINITY;
  }

  return AngularOrder.get(key) ?? Number.POSITIVE_INFINITY;
}
